import { firestore, auth, storage } from '@utils/firebase'
import { getUsersName } from '@utils/strings'
import { User, CurrentAuth } from '@Interfaces/Data/UserInterface'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { ApiResponse } from '@Interfaces/Api/ResponsesInterface'
import { ApiError } from '@Interfaces/Api/ErrorsInterface'
import {
  UnsubscribeFunction,
  ListenerCallback,
  ErrorCallback,
} from '@Interfaces/Services/ServiceTypes'

const baseURL = 'https://api.rayzeapp.com'

/**
 * Listen to user changes
 * Returns unsubscribe function
 */
export const listenToUser = (
  currentAuth: CurrentAuth,
  onUpdate: ListenerCallback<User>,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return firestore
    .collection('users')
    .doc(currentAuth.uid)
    .onSnapshot(
      (documentSnapshot) => {
        if (documentSnapshot.exists) {
          const user = {
            id: currentAuth.uid,
            ...documentSnapshot.data(),
          } as User
          onUpdate(user)
        }
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message,
          details: error,
        })
      }
    )
}

/**
 * Create user in database
 */
export const createUserInDb = async (data: User): Promise<ApiResponse<boolean>> => {
  try {
    await firestore.collection('users').doc(data?.uid).set(data)

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create user',
      data: undefined,
    }
  }
}

/**
 * Update username
 */
export const updateUsername = async (username: string): Promise<ApiResponse<boolean>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    await firestore.collection('usernames').doc(uid).set({ username, lastUpdated: new Date() })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update username',
      data: undefined,
    }
  }
}

/**
 * Check username availability
 */
export async function checkUsernameAvailability(
  username: string
): Promise<ApiResponse<{ available: boolean; error?: string }>> {
  try {
    if (!username) {
      return {
        success: false,
        error: 'Username cannot be empty',
        data: undefined,
      }
    }

    const usernameLowerCase = username?.toLowerCase()
    const usernameSnap = await firestore
      .collection('usernames')
      .where('username', '==', usernameLowerCase)
      .get()

    if (usernameSnap.size > 0) {
      return {
        success: false,
        error: 'Username already exists',
        data: { available: false, error: 'Username already exists' },
      }
    }

    return {
      success: true,
      data: { available: true },
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check username availability',
      data: undefined,
    }
  }
}

/**
 * Create user with profile picture
 */
export const createUser = async (
  data: Partial<User>,
  profilePic?: any
): Promise<ApiResponse<string>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const userData = {
      ...data,
      createdAt: new Date(),
      lastUpdated: new Date(),
      followers: 0,
      following: 0,
    }

    await firestore.collection('users').doc(uid).set(userData)

    if (profilePic) {
      await uploadProfilePicture(profilePic)
    }

    return {
      success: true,
      data: uid,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create user',
      data: undefined,
    }
  }
}

/** Loads a user doc by id; pass `setter` for callback-style updates instead of awaiting. */
export function getUser(userId: string, setter: (user: User | null) => void): void
export function getUser(userId: string): Promise<User | null>
export function getUser(userId: string, setter?: (user: User | null) => void): void | Promise<User | null> {
  const fetchUser = async (): Promise<User | null> => {
    try {
      if (!userId) {
        return null
      }

      const documentSnapshot = await firestore.collection('users').doc(userId).get()

      if (documentSnapshot.exists) {
        const data = documentSnapshot.data()
        if (!data) {
          return null
        }
        const user = {
          id: userId,
          ...data,
        } as User

        return user
      }

      return null
    } catch (error: any) {
      return null
    }
  }

  // If setter provided, use callback pattern
  if (setter !== undefined) {
    void fetchUser().then(setter)
    return
  }

  return fetchUser()
}

/**
 * Upload profile picture
 */
export const uploadProfilePicture = async (image: File | Blob): Promise<ApiResponse<string>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const profilePic = storage.ref().child('profilePictures/' + uid + '.jpg')

    const metadata = {
      cacheControl: 'max-age=31536000',
    }

    await profilePic.put(image, metadata)
    const url = await profilePic.getDownloadURL()

    return {
      success: true,
      data: url,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to upload profile picture',
      data: undefined,
    }
  }
}

/**
 * Update user data
 */
export const updateUser = async (userData: Partial<User>): Promise<ApiResponse<boolean>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    await firestore.collection('users').doc(uid).update(userData)

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update user',
      data: undefined,
    }
  }
}

/**
 * Check follow status
 */
export const checkFollowStatus = async (userId: string): Promise<ApiResponse<boolean>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const doc = await firestore.collection('user_following').doc(`${uid}-${userId}`).get()

    return {
      success: true,
      data: doc.exists && doc.data()?.active,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check follow status',
      data: undefined,
    }
  }
}

/**
 * Update follow status
 */
export const updateFollowStatus = async (
  userId: string,
  currentUser: User,
  following: boolean
): Promise<ApiResponse<boolean>> => {
  try {
    const uid = auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    await firestore
      .collection('user_following')
      .doc(`${uid}-${userId}`)
      .set({
        active: !following,
        follower: uid,
        following: userId,
        lastUpdated: new Date(),
        sendersName: getUsersName(currentUser),
        lowercaseName: getUsersName(currentUser).toLowerCase(),
      })

    return {
      success: true,
      data: !following,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update follow status',
      data: undefined,
    }
  }
}

/**
 * Get action attendees
 */
export const getActionAttendees = async (
  searchText: string,
  attendees: string[]
): Promise<ApiResponse<User[]>> => {
  try {
    const promises = attendees.map((userId) =>
      firestore
        .collection('users')
        .doc(userId)
        .get()
        .then((doc) => {
          if (doc.exists) {
            return {
              ...doc.data(),
              id: doc.id,
            } as User
          }
          return null
        })
    )

    const users = await Promise.all(promises)
    const allAttendees = users.filter((user): user is User => user !== null)

    return {
      success: true,
      data: allAttendees,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get action attendees',
      data: undefined,
    }
  }
}

/**
 * Get followers
 */
export const getFollowers = async (
  searchText: string,
  id?: string
): Promise<ApiResponse<User[]>> => {
  try {
    const uid = id || auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const search = searchText.toLowerCase()
    let query = firestore
      .collection('user_following')
      .where('following', '==', uid)
      .where('lowercaseName', '>=', search)
      .where('lowercaseName', '<=', search + '\uf8ff')
      .limit(20)

    if (search === '') {
      query = firestore.collection('user_following').where('following', '==', uid).limit(20)
    }

    const querySnapshot = await query.get()
    const promises = querySnapshot.docs.map((item) => getUsersPromise(item.data().follower))

    const users = await Promise.all(promises)
    const filteredDeleted = users.filter((x): x is User => x !== null)

    return {
      success: true,
      data: filteredDeleted,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get followers',
      data: undefined,
    }
  }
}

/**
 * Get following
 */
export const getFollowing = async (
  searchText: string,
  id?: string
): Promise<ApiResponse<User[]>> => {
  try {
    const uid = id || auth.currentUser?.uid
    if (!uid) {
      return {
        success: false,
        error: 'User not authenticated',
        data: undefined,
      }
    }

    const search = searchText.toLowerCase()
    let query = firestore
      .collection('user_following')
      .where('follower', '==', uid)
      .where('lowercaseName', '>=', search)
      .where('lowercaseName', '<=', search + '\uf8ff')
      .limit(20)

    if (search === '') {
      query = firestore.collection('user_following').where('follower', '==', uid).limit(20)
    }

    const querySnapshot = await query.get()
    const promises = querySnapshot.docs.map((item) => getUsersPromise(item.data().following))

    const users = await Promise.all(promises)
    const followers = users.filter((x): x is User => x !== null)

    return {
      success: true,
      data: followers,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get following',
      data: undefined,
    }
  }
}

/**
 * Helper: Get user promise
 */
export const getUsersPromise = (id: string): Promise<User | null> => {
  return new Promise((resolve) => {
    firestore
      .collection('users')
      .doc(id)
      .get()
      .then((doc) => {
        if (doc.exists) {
          const data = doc.data()
          if (!data) {
            resolve(null)
            return
          }
          const user = {
            id: doc.id,
            ...data,
          } as User
          resolve(user)
        } else {
          resolve(null)
        }
      })
      .catch(() => {
        resolve(null)
      })
  })
}

/**
 * Helper: Get users from members promise
 */
export const getUsersFromMembersPromise = (userId: string, obj: any): Promise<User | null> => {
  return new Promise((resolve) => {
    firestore
      .collection('users')
      .doc(userId)
      .get()
      .then((doc) => {
        if (doc.exists) {
          const data = doc.data()
          if (!data) {
            resolve(null)
            return
          }
          if (data.birthday && typeof data.birthday === 'object' && 'toDate' in data.birthday) {
            data.birthday = (data.birthday as { toDate: () => Date }).toDate()
          }
          const user = {
            ...obj,
            ...data,
          } as User
          resolve(user)
        } else {
          resolve(null)
        }
      })
      .catch(() => {
        resolve(null)
      })
  })
}

/**
 * Get user email
 */
export const getUserEmail = async (uid: string): Promise<ApiResponse<string>> => {
  try {
    const url = baseURL + '/getUserEmail/' + uid

    const response = await fetch(url, {
      method: 'GET',
    })

    const data = await response.json()

    return {
      success: true,
      data: data?.email,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get user email',
      data: undefined,
    }
  }
}

/**
 * Update member info
 */
export const updateMemberInfo = async (
  org: Org,
  user: User,
  data: any
): Promise<ApiResponse<boolean>> => {
  try {
    await firestore
      .collection('organizations')
      .doc(org.id)
      .collection('members')
      .doc(user.id)
      .update(data)

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update member info',
      data: undefined,
    }
  }
}

/**
 * Get profile picture
 */
export const getProfilePicture = async (uid: string): Promise<ApiResponse<string | null>> => {
  try {
    const profilePic = storage.ref().child('profilePictures/' + uid + '.jpg')
    const url = await profilePic.getDownloadURL()

    return {
      success: true,
      data: url,
    }
  } catch (error: any) {
    return {
      success: true,
      data: null,
    }
  }
}
